import { PremiumProduct } from '../../types/car-insurance';
import { CarInsurance, PeriodGift, VerticalType } from '../../types/cmsTypes';
import { MaterialIconName } from '../../types/materialIconName';
import { TravelPremiumsData } from '../../types/travelInsurance';
import { CmsClientContextValue } from '../../utils/cmsPageContext';
import {
  formatNumberWithSeparator,
  currencyFormat,
  PersonalLoanFilterConfig,
} from '../../utils/helpers';
import {
  calculateRewardValue,
  calculateOffersValue,
  UrgencyPhase,
  getCommonUrgencyPhase,
} from '../../utils/helpers/calculateRewardValue';
import {
  getApplyThrough,
  getTitleWelcomeOffer,
  getWelcomeGiftInfo,
} from '../../utils/helpers/offerGroupLabels';
import {
  filterOffersByAmountAndTenure,
  getChooseOneOffers,
  mapOfferToPeriodGifts,
} from '../../utils/helpers/offers';
import { LoanSearchParams } from '../../utils/searchParams/loanSearchParams';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { tr } from '../../utils/translations/tr';
import { CarInsuranceCtaBuilderInstance } from '../CarInsurance/CarInsuranceCtaBuilder';
import { TravelInsuranceCtaType } from '../TravelInsuranceCtaBuilder';
import {
  ICON_SECTION_DETAIL_MAP,
  PrefixCurrencyType,
  TITLE_SECTION_DETAIL_MAP,
} from './constants';
import { TranslationKey, TRANSLATIONS } from './i18n';
import { CarInsuranceQuote } from './SectionPremiumCI';
import {
  BuildSectionOfferData,
  EligibilityOffer,
  ProductCardProps,
  SectionType,
} from './types';

function getFromMap<K extends string | number | symbol, V>(
  map: Partial<Record<K, V>>,
  key: K,
  defaultValue: V,
): V {
  return map[key] ?? defaultValue;
}

export function getTitleSectionDetail(vertical: VerticalType): TranslationKey {
  return getFromMap(TITLE_SECTION_DETAIL_MAP, vertical, 'card_details');
}

export function getIconSectionDetail(
  vertical: VerticalType,
): MaterialIconName | undefined {
  return ICON_SECTION_DETAIL_MAP[vertical];
}

export function buildSectionGiftComponentKey(
  productTitle: string,
  type: 'welcome' | 'exclusive',
  productDetailsModalKeys: string[],
): string {
  const base = `ProductCard_${productTitle}_SectionGift_${type}`;
  return productDetailsModalKeys.includes(productTitle)
    ? `${base}_productDetail`
    : base;
}

export const buildSectionWelcomeOfferData = (props: {
  productCardProps: ProductCardProps;
  prefix?: PrefixCurrencyType;
  context: CmsClientContextValue;
  loanSearchParams: LoanSearchParams | undefined;
  vertical: VerticalType;
  isVisible: boolean;
  offersWithFilteredGifts: Array<EligibilityOffer>;
  plFilterConfig: PersonalLoanFilterConfig | undefined;
  toggleSectionVisible: (section: SectionType, titleSection?: string) => void;
  carInsuranceCtaComponent: CarInsuranceCtaBuilderInstance | null;
  priceLine: React.ReactNode | null;
  productDetailsModalKeys?: string[];
  travelInsuranceCtaComponent?: (
    travelInsuranceCtaType: TravelInsuranceCtaType,
  ) => JSX.Element | undefined;
}): BuildSectionOfferData => {
  const {
    productCardProps: {
      product: { welcome_offers },
    },
    context,
    prefix,
    loanSearchParams,
    vertical,
    offersWithFilteredGifts,
    isVisible,
    plFilterConfig,
    toggleSectionVisible,
    carInsuranceCtaComponent,
    priceLine,
    productDetailsModalKeys = [],
    travelInsuranceCtaComponent,
  } = props;
  const staticText = getClientTranslations(TRANSLATIONS, context);

  const giftsWelcomeOffer =
    mapOfferToPeriodGifts(offersWithFilteredGifts) ?? [];

  const ineligibleGiftsExclusiveOffer =
    mapOfferToPeriodGifts(offersWithFilteredGifts, true) ?? [];

  const titleSectionWelcomeOffer = getTitleWelcomeOffer(vertical);
  const title = tr(titleSectionWelcomeOffer, staticText);

  const defaultGiftInfo = tr(getWelcomeGiftInfo(vertical), staticText);

  const sameUrgencyPhaseExclusiveOffer: UrgencyPhase | undefined =
    getCommonUrgencyPhase(welcome_offers ?? []);

  return buildSectionOfferData({
    type: 'welcome',
    productCardProps: props.productCardProps,
    context,
    prefix,
    loanSearchParams,
    vertical,
    isVisible,
    toggleVisibility: () =>
      toggleSectionVisible(
        'rewardWelcome',
        tr(titleSectionWelcomeOffer, staticText),
      ),
    title,
    defaultGiftInfo,
    giftLinkText: tr('full_reward', staticText),
    componentUniqueKey: buildSectionGiftComponentKey(
      props.productCardProps.product.title ?? '',
      'welcome',
      productDetailsModalKeys,
    ),
    offersPeriodGift: giftsWelcomeOffer,
    ineligibleGiftsOffer: ineligibleGiftsExclusiveOffer,
    sameUrgencyPhaseOffer: sameUrgencyPhaseExclusiveOffer,
    carInsuranceCtaComponent,
    priceLine,
    plFilterConfig,
    travelInsuranceCtaComponent,
  });
};

export const buildSectionExclusiveOfferData = (props: {
  productCardProps: ProductCardProps;
  prefix: PrefixCurrencyType;
  context: CmsClientContextValue;
  loanSearchParams: LoanSearchParams | undefined;
  vertical: VerticalType;
  isVisible: boolean;
  offersWithFilteredGifts: Array<EligibilityOffer>;
  plFilterConfig: PersonalLoanFilterConfig | undefined;
  toggleSectionVisible: (section: SectionType, titleSection?: string) => void;
  carInsuranceCtaComponent: CarInsuranceCtaBuilderInstance | null;
  priceLine: React.ReactNode | null;
  productDetailsModalKeys?: string[];
  travelInsuranceCtaComponent?: (
    travelInsuranceCtaType: TravelInsuranceCtaType,
  ) => JSX.Element | undefined;
}): BuildSectionOfferData => {
  const {
    productCardProps: {
      product: { welcome_offers, attributes: productAttributes },
    },
    context,
    prefix,
    loanSearchParams,
    vertical,
    offersWithFilteredGifts,
    isVisible,
    plFilterConfig,
    toggleSectionVisible,
    carInsuranceCtaComponent,
    priceLine,
    productDetailsModalKeys = [],
    travelInsuranceCtaComponent,
  } = props;
  const staticText = getClientTranslations(TRANSLATIONS, context);
  const hasPersonalLoanParams =
    !!loanSearchParams?.amount || !!loanSearchParams?.tenure;

  const welcomeOffers = filterOffersByAmountAndTenure(
    welcome_offers ?? [],
    loanSearchParams?.amount?.toString(),
    loanSearchParams?.tenure?.toString(),
  );
  const exclusiveAndBundleOffers = getChooseOneOffers(
    props.productCardProps.product,
  );
  const exclusiveOffers = filterOffersByAmountAndTenure(
    exclusiveAndBundleOffers,
    loanSearchParams?.amount?.toString(),
    loanSearchParams?.tenure?.toString(),
  );

  const singSaverRewardUpTo = formatNumberWithSeparator({
    value: calculateRewardValue({
      welcomeOffers: welcomeOffers ?? [],
      exclusiveOffers: exclusiveOffers ?? [],
    }),
    thousandsSeparator: ',',
  });

  const giftsExclusiveOffer =
    mapOfferToPeriodGifts(offersWithFilteredGifts) ?? [];

  const ineligibleGiftsExclusiveOffer =
    mapOfferToPeriodGifts(offersWithFilteredGifts, true) ?? [];

  const rewardElseWhere = productAttributes
    ?.filter((a) => a.__typename === 'ComponentAttributesAttrNumber')
    .find((a) => a.name?.name === 'REWARD_ELSEWHERE');

  const shouldShowEligibilityHint =
    ineligibleGiftsExclusiveOffer?.length > 0 &&
    giftsExclusiveOffer?.length === 0;

  const title = shouldShowEligibilityHint
    ? staticText.brand_reward_guidance
    : [
        staticText.brand_reward,
        singSaverRewardUpTo !== '0' &&
          `${tr('up_to', staticText)} ${currencyFormat(prefix)}${singSaverRewardUpTo}`,
      ]
        .filter(Boolean)
        .join(' ');

  const subtitle = [
    rewardElseWhere?.value !== '0' &&
      rewardElseWhere?.value &&
      `${tr('with_others', staticText)} ${rewardElseWhere.prefix}${rewardElseWhere.value}`,
  ]
    .filter(Boolean)
    .join('');

  const loanExclusiveInfo =
    hasPersonalLoanParams && vertical === 'PERSONAL_LOANS'
      ? tr('loan_amount_exclusive', staticText, {
          product: props.productCardProps.product.product_name ?? '',
          amount: formatNumberWithSeparator({
            value: loanSearchParams?.amount ?? 0,
            thousandsSeparator: ',',
          }),
          tenure: loanSearchParams?.tenure ?? '',
          brand: context.brand[0].toUpperCase() + context.brand.slice(1),
          currency: currencyFormat(prefix),
        })
      : null;

  const defaultGiftInfo =
    loanExclusiveInfo ??
    tr(getApplyThrough(vertical), staticText, {
      count: giftsExclusiveOffer?.length.toString() ?? '',
    });

  const sameUrgencyPhaseExclusiveOffer: UrgencyPhase | undefined =
    vertical === 'PERSONAL_LOANS'
      ? getCommonUrgencyPhase(offersWithFilteredGifts ?? [])
      : getCommonUrgencyPhase(exclusiveAndBundleOffers);

  return buildSectionOfferData({
    type: 'exclusive',
    productCardProps: props.productCardProps,
    context,
    prefix,
    loanSearchParams,
    vertical,
    isVisible,
    toggleVisibility: () =>
      toggleSectionVisible('rewardExclusive', title + subtitle),
    title,
    defaultSubtitle: subtitle,
    defaultGiftInfo,
    giftLinkText: tr('full_reward', staticText),
    componentUniqueKey: buildSectionGiftComponentKey(
      props.productCardProps.product.title ?? '',
      'exclusive',
      productDetailsModalKeys,
    ),
    offersPeriodGift: giftsExclusiveOffer,
    ineligibleGiftsOffer: ineligibleGiftsExclusiveOffer,
    sameUrgencyPhaseOffer: sameUrgencyPhaseExclusiveOffer,
    carInsuranceCtaComponent,
    priceLine,
    plFilterConfig,
    travelInsuranceCtaComponent,
  });
};

export const buildSectionOfferData = (props: {
  type: 'welcome' | 'exclusive';
  productCardProps: ProductCardProps;
  context: CmsClientContextValue;
  prefix?: PrefixCurrencyType;
  toggleVisibility: () => void;
  isVisible: boolean;
  loanSearchParams: LoanSearchParams | undefined;
  vertical: VerticalType;
  title: string;
  defaultSubtitle?: string;
  defaultGiftInfo: string;
  giftLinkText: string;
  componentUniqueKey: string;
  offersPeriodGift: Array<PeriodGift>;
  ineligibleGiftsOffer: Array<PeriodGift>;
  sameUrgencyPhaseOffer?: UrgencyPhase;
  carInsuranceCtaComponent: CarInsuranceCtaBuilderInstance | null;
  priceLine: React.ReactNode | null;
  plFilterConfig: PersonalLoanFilterConfig | undefined;
  travelInsuranceCtaComponent?: (
    travelInsuranceCtaType: TravelInsuranceCtaType,
  ) => JSX.Element | undefined;
}): BuildSectionOfferData => {
  const {
    productCardProps: {
      product: { welcome_offers },
      ctaLinkOverride,
      pushDataLayer,
      approvalOdds,
      approvalOddsState,
    },
    prefix,
    context,
    loanSearchParams,
    carInsuranceCtaComponent,
    priceLine,
    vertical,
    isVisible,
    toggleVisibility,
    title,
    offersPeriodGift,
    ineligibleGiftsOffer,
    defaultGiftInfo,
    giftLinkText,
    componentUniqueKey,
    sameUrgencyPhaseOffer,
    type,
    defaultSubtitle = '',
    plFilterConfig,
    travelInsuranceCtaComponent,
  } = props;

  const staticText = getClientTranslations(TRANSLATIONS, context);

  const exclusiveAndBundleOffers = getChooseOneOffers(
    props.productCardProps.product,
  );

  const shouldShowEligibilityHint =
    ineligibleGiftsOffer?.length > 0 && offersPeriodGift?.length === 0;

  const subtitle = shouldShowEligibilityHint
    ? tr('update_amount_tenure', staticText)
    : defaultSubtitle;

  /**
   * Note: Gift eligibility intervals for loan amount and tenure may not overlap between different gifts.
   * Therefore, it is not possible to simply pick the minimum values across all gifts.
   * Each gift may have its own distinct criteria, so thresholds need to be evaluated per gift.
   */

  const minEligibleGift = ineligibleGiftsOffer.reduce<{
    minAmount: number;
    gift?: PeriodGift;
  }>(
    (accum, gift) => {
      if (
        gift.minAmount != null &&
        Number.isFinite(gift.minAmount) &&
        gift.minAmount < accum.minAmount
      ) {
        accum.minAmount = gift.minAmount;
        accum.gift = gift;
      }
      return accum;
    },
    { minAmount: Infinity, gift: undefined },
  );

  const minWelcomeOffers = filterOffersByAmountAndTenure(
    welcome_offers ?? [],
    minEligibleGift.gift?.minAmount?.toString(),
    minEligibleGift.gift?.minTenure?.toString(),
  );
  const minExclusiveOffers = filterOffersByAmountAndTenure(
    exclusiveAndBundleOffers,
    minEligibleGift.gift?.minAmount?.toString(),
    minEligibleGift.gift?.minTenure?.toString(),
  );

  const offersMatchingMinThreshold =
    (type === 'exclusive' ? minExclusiveOffers : minWelcomeOffers) ?? [];

  const maxRewardValueForDisplay = calculateOffersValue(
    offersMatchingMinThreshold,
  );

  const welcomeOffers = filterOffersByAmountAndTenure(
    welcome_offers ?? [],
    loanSearchParams?.amount?.toString(),
    loanSearchParams?.tenure?.toString(),
  );
  const exclusiveOffers = filterOffersByAmountAndTenure(
    exclusiveAndBundleOffers,
    loanSearchParams?.amount?.toString(),
    loanSearchParams?.tenure?.toString(),
  );

  const getTranslation = () => {
    const hasAmount = !!minEligibleGift.gift?.minAmount;
    const hasTenure = !!minEligibleGift.gift?.minTenure;
    const hasRewardValue = !!maxRewardValueForDisplay;

    if (hasAmount && hasTenure && hasRewardValue)
      return 'amount_tenure_price_unlock_reward';

    if (hasAmount && hasTenure) return 'amount_tenure_unlock_reward';

    if (hasAmount) return 'amount_unlock_reward';

    return 'tenure_unlock_reward';
  };

  const eligibilityHint = tr(getTranslation(), staticText, {
    amount: formatNumberWithSeparator({
      value:
        minEligibleGift.gift?.minAmount ?? plFilterConfig?.min_loan_amount ?? 0,
      thousandsSeparator: ',',
    }),
    tenure:
      minEligibleGift.gift?.minTenure?.toString() ||
      plFilterConfig?.min_loan_tenure?.toString() ||
      '',
    prefix: prefix ?? '',
    maxReward: maxRewardValueForDisplay?.toString(),
  });

  const giftsInfo = (() => {
    if (shouldShowEligibilityHint) {
      return eligibilityHint;
    }
    return defaultGiftInfo;
  })();

  return {
    data: {
      title,
      subtitle,
      isVisible,
      toggleVisibility,
      giftsInfo,
      vertical,
      loanSearchParams,
      carInsuranceCtaComponent,
      priceLine,
      ctaLinkOverride,
      product: props.productCardProps.product,
      sameUrgencyPhaseOffer,
      giftLinkText,
      type,
      componentUniqueKey,
      exclusiveOffers: exclusiveOffers ?? [],
      welcomeOffers: welcomeOffers ?? [],
      gifts: offersPeriodGift,
      pushDataLayer,
      travelInsuranceCtaComponent,   
      approvalOdds,
      approvalOddsState,   
    },
    shouldShowEligibilityHint,
    maxRewardValueForDisplay,
    minEligibleGift: minEligibleGift.gift,
    shouldRenderSection: (offersPeriodGift?.length ?? 0) > 0,
  };
};

// Utility function copied from car-insurance/utils/getPrice.ts
export const getPrice = (carInsuranceQuote: CarInsuranceQuote): number => {
  const taxes = Array.from(Object.values(carInsuranceQuote.taxes));
  const premiumPrices = Array.from(Object.values(carInsuranceQuote.prices));

  const formattedPremiumPrices = premiumPrices.map((item) =>
    formatPrice(item.price),
  );

  const netPremium = formattedPremiumPrices.reduce(
    (prev, curr) => prev + curr,
    0,
  );
  const totalTaxSum = taxes
    .map((item) => Number(item.percentage))
    .reduce((prev, curr) => prev + (curr / 100) * netPremium, 0);
  const totalPremium = Number((netPremium + totalTaxSum).toFixed(0));

  return totalPremium;
};

const formatPrice = (price: number): number => {
  return price % 100 === 0 ? price / 100 : price;
};

export const getCiPriceValue = (
  productWithLivePrice?: PremiumProduct,
  carInsuranceQuote?: CarInsuranceQuote,
  carInsuranceSection?: CarInsurance,
) => {
  return (
    productWithLivePrice?.discountValue ??
    productWithLivePrice?.duePremium ??
    (carInsuranceQuote && getPrice(carInsuranceQuote)) ??
    carInsuranceSection?.priceStatic ??
    0
  );
};

export const getTiPriceValue = (
  travelInsuranceQuotes?: TravelPremiumsData[0],
): number => {
  if (!travelInsuranceQuotes) return 0;

  const { offeredPremium, originalPremium } = travelInsuranceQuotes;

  const offeredValue = parseFloat(offeredPremium ?? '');
  if (!isNaN(offeredValue) && offeredValue > 0) {
    return offeredValue;
  }

  const originalValue = parseFloat(originalPremium ?? '');
  if (!isNaN(originalValue) && originalValue > 0) {
    return originalValue;
  }

  return 0;
};
